import { useState, useEffect, useCallback } from 'react';
import api, { BACKEND_URL } from '../../api/axios';

const DOC_LABELS = {
  gst: 'GST Certificate',
  cin: 'Certificate of Incorporation',
  udyam: 'Udyam / Udyog Aadhaar',
  pan: 'PAN Card',
  aadhaar: 'Personal Aadhaar',
  license: 'Company License',
};

const DOC_STATUS_META = {
  pending: { label: 'Pending', cls: 'bg-[#FFFBEB] text-[#F59E0B]' },
  approved: { label: 'Approved', cls: 'bg-[#ECFDF5] text-[#10B981]' },
  rejected: { label: 'Rejected', cls: 'bg-[#FEF2F2] text-[#EF4444]' },
  resubmission_requested: { label: 'Resubmission Requested', cls: 'bg-[#FEF2F2] text-[#EF4444]' },
};

const DOC_FILTERS = [
  { key: '', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'resubmission_requested', label: 'Resubmission Requested' },
];

function resolveMediaUrl(path) {
  if (!path) return null;
  return path.startsWith('http') ? path : `${BACKEND_URL}/storage/${path}`;
}

/**
 * Aggregates documents across all employers by fetching each employer's
 * detail record (since there's no single "all documents" endpoint yet).
 * Swap the `load` body for a single GET `${apiPrefix}/documents` call
 * if/when that endpoint exists — it'll be much faster.
 */
function VerificationDocumentsTable({ apiPrefix, allowRedecide = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await api.get(`${apiPrefix}/documents`);
      const data = Array.isArray(response.data) ? response.data : (response.data?.data ?? []);
      setRows(data);
    } catch (err) {
      console.error('Failed to load documents', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [apiPrefix]);

  useEffect(() => { load(); }, [load]);

  const act = async (docId, action) => {
    setBusyId(docId);
    try {
      const endpoint = action === 'approve'
        ? `${apiPrefix}/documents/${docId}/approve`
        : `${apiPrefix}/documents/${docId}/reject`;
      await api.put(endpoint, action === 'reject' ? { remarks: 'Rejected by reviewer' } : {});
      await load();
    } catch (err) {
      alert(err.response?.data?.message || 'Action failed.');
    } finally {
      setBusyId(null);
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = rows
    .filter((r) => (filter ? r.status === filter : true))
    .filter((r) => {
      if (!q) return true;
      const employerName = (r.employer?.name || '').toLowerCase();
      const employerEmail = (r.employer?.email || '').toLowerCase();
      const docType = (DOC_LABELS[r.document_type] || r.document_type || '').toLowerCase();
      const docNumber = (r.document_number || '').toLowerCase();
      return (
        employerName.includes(q) ||
        employerEmail.includes(q) ||
        docType.includes(q) ||
        docNumber.includes(q)
      );
    });

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by employer, document type, or number..."
            className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3D] focus:border-transparent"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
              aria-label="Clear search"
            >
              &times;
            </button>
          )}
        </div>
        {q && (
          <span className="text-xs text-gray-400">
            {filtered.length} of {rows.length} shown
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {DOC_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
              filter === f.key
                ? 'bg-[#0D1B3D] text-white border-[#0D1B3D]'
                : 'bg-white text-gray-600 border-gray-300 hover:border-[#0D1B3D]'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400 text-xs uppercase border-b border-gray-100 bg-[#F0F5FF]">
              <th className="px-5 py-3 font-medium">Employer</th>
              <th className="px-5 py-3 font-medium">Document Type</th>
              <th className="px-5 py-3 font-medium">Document Number</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium">File</th>
              <th className="px-5 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="py-6 text-center text-gray-400">Loading&hellip;</td></tr>
            )}
            {!loading && loadError && (
              <tr><td colSpan={6} className="py-6 text-center text-red-500">
                Could not load documents. <button onClick={load} className="underline">Retry</button>
              </td></tr>
            )}
            {!loading && !loadError && filtered.length === 0 && (
              <tr><td colSpan={6} className="py-6 text-center text-gray-400">
                {q ? 'No documents match your search.' : 'No documents found.'}
              </td></tr>
            )}
            {!loading && !loadError && filtered.map((doc) => {
              const meta = DOC_STATUS_META[doc.status] || DOC_STATUS_META.pending;
              const fileUrl = resolveMediaUrl(doc.file_path);
              const isBusy = busyId === doc.id;
              return (
                <tr key={doc.id} className="border-t border-gray-50">
                  <td className="px-5 py-3">
                    <p className="font-semibold text-[#0D1B3D]">{doc.employer?.name}</p>
                    <p className="text-xs text-gray-400">{doc.employer?.email}</p>
                  </td>
                  <td className="px-5 py-3">{DOC_LABELS[doc.document_type] || doc.document_type}</td>
                  <td className="px-5 py-3">{doc.document_number || '\u2014'}</td>
                  <td className="px-5 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${meta.cls}`}>{meta.label}</span>
                  </td>
                  <td className="px-5 py-3">
                    {fileUrl ? (
                      <a href={fileUrl} target="_blank" rel="noreferrer" className="text-[#2563EB] underline text-xs">
                        View File
                      </a>
                    ) : '\u2014'}
                  </td>
                  <td className="px-5 py-3">
                    {doc.status !== 'pending' && !allowRedecide ? (
                      <span className="text-xs text-gray-400">&mdash;</span>
                    ) : (
                      <div className="flex gap-3">
                        <button
                          disabled={isBusy}
                          onClick={() => act(doc.id, 'approve')}
                          className="text-[#10B981] text-xs font-semibold hover:underline disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button
                          disabled={isBusy}
                          onClick={() => act(doc.id, 'reject')}
                          className="text-[#EF4444] text-xs font-semibold hover:underline disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default VerificationDocumentsTable;